/**
 * Metas do dia no modo ao vivo (Hoje e TV): a meta mensal salva em Ajustes › Metas vira a meta do
 * dia (pelos pesos por dia da semana), o ritmo e a projeção com as vendas reais da Kiwify.
 *
 * As contas são as de `packages/metrics/src/pacing.ts` (SPEC 5.11), copiadas: a fronteira 10.2 não
 * deixa o app importar `@rota/metrics`. O que muda no modo ao vivo é de onde vem cada entrada:
 * - Faturamento = valor dos produtos das vendas aprovadas hoje (sem os juros do parcelamento).
 * - Vendas = cursos pagos hoje; a meta de vendas usa o faturamento por comprador do curso medido nos
 *   últimos 28 dias (sem comprador, o preço do curso dos Ajustes).
 * - Curva do dia (F) = a dos mesmos dias da semana com venda nas últimas 4 semanas; sem histórico, a
 *   curva padrão da SPEC.
 * - Projeção = realizado + (1 − F) × faturamento típico do dia (média dos dias com venda nos últimos
 *   28 dias; com menos de 7 dias de histórico, a própria meta do dia, como na SPEC).
 * - Lucro = resultado da empresa de hoje (a mesma conta da DRE): valor dos produtos − taxa da Kiwify −
 *   imposto cadastrado − reembolsos − custo real da mídia. Sem a Meta, fica nulo (sem `finance:read`,
 *   a tela não mostra o anel do lucro).
 * Sem meta salva (0), nenhuma meta: o Hoje mostra "Defina a meta".
 */
import type { GoalProgress, PaceState, Settings } from '@rota/contracts';
import { DAY, dayStart, HOUR, isSold, type RealOrderRow, revenueOf, todayKey } from './realCore';

// ---------- curva do dia ----------

/** Curva padrão do faturamento (% por hora, 0h..23h) até haver histórico (SPEC 5.11). */
export const REVENUE_HOURLY_PCT_DEFAULT: readonly number[] = [
  3.4, 1.8, 1.0, 0.6, 0.5, 0.6, 1.2, 2.2, 3.4, 4.2, 5.0, 5.2, 5.5, 5.3, 5.2, 5.2, 5.2, 5.4, 5.9, 6.6, 7.2,
  7.4, 6.8, 5.2,
];

/** `cum[h]` = fração até o fim da hora h; `hourly[h]` = fração da hora h. */
export interface DayCurve {
  hourly: readonly number[];
  cum: readonly number[];
}

export function buildCurve(weights: readonly number[]): DayCurve {
  const w = Array.from({ length: 24 }, (_, h) => {
    const x = weights[h] ?? 0;
    return Number.isFinite(x) && x > 0 ? x : 0;
  });
  const total = w.reduce((a, b) => a + b, 0);
  const hourly = total > 0 ? w.map((x) => x / total) : w.map(() => 1 / 24);
  const cum: number[] = [];
  let acc = 0;
  for (const h of hourly) {
    acc += h;
    cum.push(acc);
  }
  cum[23] = 1;
  return { hourly, cum };
}

export const REVENUE_CURVE_DEFAULT: DayCurve = buildCurve(REVENUE_HOURLY_PCT_DEFAULT);

/** Média das frações por hora dos dias dados (até os 4 mais recentes), suavizada [¼, ½, ¼]. */
export function curveFromHistory(days: readonly (readonly number[])[], fallback: DayCurve): DayCurve {
  const valid = days.filter((d) => d.length === 24 && d.some((v) => v > 0)).slice(-4);
  if (valid.length === 0) return fallback;
  const avg = new Array<number>(24).fill(0);
  for (const d of valid) {
    const total = d.reduce((a, b) => a + Math.max(0, b), 0);
    for (let h = 0; h < 24; h++) avg[h] = (avg[h] ?? 0) + Math.max(0, d[h] ?? 0) / total / valid.length;
  }
  return buildCurve(
    avg.map((v, h) => 0.25 * (avg[(h + 23) % 24] ?? 0) + 0.5 * v + 0.25 * (avg[(h + 1) % 24] ?? 0)),
  );
}

/** F num minuto do dia (0..1440), linear dentro da hora. */
export function curveAt(curve: DayCurve, minuteOfDay: number): number {
  if (minuteOfDay <= 0) return 0;
  if (minuteOfDay >= 1440) return 1;
  const h = Math.floor(minuteOfDay / 60);
  const before = h > 0 ? (curve.cum[h - 1] ?? 0) : 0;
  return before + (curve.hourly[h] ?? 0) * ((minuteOfDay - h * 60) / 60);
}

// ---------- meta do dia, ritmo e projeção ----------

/** Dia da semana (0 = domingo) de um dia AAAA-MM-DD. */
const weekdayOf = (key: string): number => new Date(`${key}T12:00:00Z`).getUTCDay();

/** Meta do dia = meta mensal × peso do dia ÷ Σ pesos dos dias do mês. */
export function goalRevenueDay(monthly: number, key: string, weights: readonly number[]): number {
  const [y, m] = key.split('-').map(Number) as [number, number];
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const weight = (k: string): number => {
    const w = weights[weekdayOf(k)];
    return w != null && Number.isFinite(w) && w >= 0 ? w : 1;
  };
  let total = 0;
  for (let d = 1; d <= days; d++) total += weight(`${key.slice(0, 8)}${String(d).padStart(2, '0')}`);
  return total > 0 ? Math.round((monthly * weight(key)) / total) : 0;
}

/** Ritmo = realizado ÷ (meta × F); sem esperado (F = 0), null. */
export function pace(actual: number, goal: number, f: number): number | null {
  const expected = goal * f;
  return expected > 0 ? actual / expected : null;
}

/** "à frente" ≥ 1,0 · "no ritmo" 0,9–1,0 · "atrás" < 0,9. */
export function paceState(p: number | null): PaceState {
  if (p == null) return 'on_track';
  if (p >= 1) return 'ahead';
  return p >= 0.9 ? 'on_track' : 'behind';
}

/** Projeção até 23:59 com banda de 80%: ± 1,28 × √(vendas restantes esperadas) × ticket. */
export function projectEndOfDay(i: { actual: number; f: number; baselineDay: number; aov: number }) {
  const f = Math.min(1, Math.max(0, i.f));
  const value = Math.round(i.actual + (1 - f) * Math.max(0, i.baselineDay));
  const remainingSales = i.aov > 0 ? Math.max(0, value - i.actual) / i.aov : 0;
  const half = Math.round(1.28 * Math.sqrt(remainingSales) * i.aov);
  return { value, low: Math.max(i.actual, value - half), high: value + half };
}

const toMin = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const hhmmOf = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** Próximo checkpoint depois de agora (hoje; senão o primeiro de amanhã), em São Paulo. */
export function nextCheckpoint(now: number, checkpoints: readonly string[]): { at: number; label: string } {
  const start = dayStart(todayKey(now));
  const sorted = [...(checkpoints.length ? checkpoints : ['09:00'])]
    .filter((c) => /^([01]\d|2[0-3]):[0-5]\d$/.test(c))
    .sort((a, b) => toMin(a) - toMin(b));
  for (const c of sorted) {
    const at = start + toMin(c) * 60_000;
    if (at > now) return { at, label: hhmmOf(toMin(c)) };
  }
  const first = sorted[0] ?? '09:00';
  return { at: start + DAY + toMin(first) * 60_000, label: hhmmOf(toMin(first)) };
}

// ---------- histórico real ----------

/** O que o histórico dos últimos 28 dias diz: ticket, dia típico e a curva do mesmo dia da semana. */
export interface GoalHistory {
  /** Faturamento por comprador do curso (valor dos produtos ÷ cursos vendidos); null sem curso. */
  aov: number | null;
  /** Faturamento médio dos dias com histórico; null com menos de 7 dias. */
  baselineDay: number | null;
  curve: DayCurve;
}

/** Dias mínimos de histórico para o dia típico valer na projeção (antes disso, a meta do dia). */
export const MIN_BASELINE_DAYS = 7;

/**
 * Lê os pedidos dos dias antes de hoje (até 28): vendas aprovadas (pagas ou reembolsadas depois) no
 * valor dos produtos, como o faturamento do Hoje. O histórico começa no dia da primeira venda.
 */
export function goalHistory(rows: readonly RealOrderRow[], now: number): GoalHistory {
  const today = dayStart(todayKey(now));
  const sold = rows.filter((o) => isSold(o) && o.at < today);
  let revenue = 0;
  let front = 0;
  const byDay = new Map<string, number[]>();
  for (const o of sold) {
    revenue += revenueOf(o);
    if (o.kind === 'front') front++;
    const key = todayKey(o.at);
    const hours = byDay.get(key) ?? new Array<number>(24).fill(0);
    const h = Math.floor((o.at - dayStart(key)) / HOUR);
    hours[h] = (hours[h] ?? 0) + revenueOf(o);
    byDay.set(key, hours);
  }
  const first = sold.length ? Math.min(...sold.map((o) => dayStart(todayKey(o.at)))) : today;
  const days = Math.round((today - first) / DAY);
  const wd = weekdayOf(todayKey(now));
  const same = [...byDay.entries()]
    .filter(([k]) => weekdayOf(k) === wd)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
  return {
    aov: front > 0 ? Math.round(revenue / front) : null,
    baselineDay: days >= MIN_BASELINE_DAYS ? Math.round(revenue / days) : null,
    curve: curveFromHistory(same, REVENUE_CURVE_DEFAULT),
  };
}

// ---------- GoalProgress do contrato ----------

export interface LiveGoalsInput {
  now: number;
  goals: Settings['goals'];
  checkpoints: readonly string[];
  /** Hoje até agora: faturamento (valor dos produtos), cursos pagos e resultado da empresa (ou null). */
  actual: { revenue: number; sales: number; profit: number | null };
  history: GoalHistory;
  /** Preço do curso (Ajustes): ticket quando ainda não há comprador do curso para medir. */
  priceFront: number;
}

/** Monta o `GoalProgress` do Hoje com as vendas reais; meta mensal 0 = sem meta (null). */
export function buildLiveGoals(i: LiveGoalsInput): GoalProgress | null {
  if (!(i.goals.monthlyRevenue > 0)) return null;
  const key = todayKey(i.now);
  const minute = (i.now - dayStart(key)) / 60_000;
  const f = curveAt(i.history.curve, minute);
  const revGoal = goalRevenueDay(i.goals.monthlyRevenue, key, i.goals.weekdayWeights);
  const aov = i.history.aov ?? (i.priceFront > 0 ? i.priceFront : 0);
  const salesGoal = aov > 0 ? Math.round(revGoal / aov) : 0;
  const proj = projectEndOfDay({
    actual: i.actual.revenue,
    f,
    baselineDay: i.history.baselineDay ?? revGoal,
    aov,
  });
  const line = (goal: number, actual: number) => {
    const p = pace(actual, goal, f);
    return { goal, actual, expectedNow: Math.round(goal * f), pace: p ?? 0, state: paceState(p) };
  };
  return {
    dateKey: key,
    revenue: line(revGoal, i.actual.revenue),
    profit:
      i.actual.profit == null ? null : line(Math.round(i.goals.targetMargin * revGoal), i.actual.profit),
    sales: line(salesGoal, i.actual.sales),
    // Lucro projetado: sem medida do lucro do resto do dia, fica vazio (nunca estimado às cegas).
    projection: { revenue: proj.value, low: proj.low, high: proj.high, profit: null },
    fNow: f,
    nextCheckpoint: nextCheckpoint(i.now, i.checkpoints),
  };
}
